import { Box, Stack, Typography } from "@mui/material";

import { Control, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  SelectInput,
  SliderInput,
  SwitchInput,
  TextInput,
} from "components/forms";
import { SectionCard } from "components/SectionCard/SectionCard";
import { DEVICE_TYPE_SOMNOFY_SM100 } from "constants/deviceConstants";
import { TIMEZONES } from "utils/timezones";

import { FieldColumns } from "../../components/FieldColumns";
import { DeviceFormSchema } from "../forms/deviceFormSchema";

/**
 * PVBT-142
 * Setting the min distance below this threshold prevents devices from booting
 */
const XETHRU_CHIP_MIN_DISTANCE = 0.6;

type DeviceSettingsProps = {
  deviceType: DeviceFormSchema["type"];
  control: Control<DeviceFormSchema>;
};

export function DeviceSettings({ deviceType, control }: DeviceSettingsProps) {
  const { t } = useTranslation();

  const isSM100 = deviceType === DEVICE_TYPE_SOMNOFY_SM100;

  const disableSleepTracking = useWatch({
    control,
    name: "settings.user.schedule.0.enabled",
  });

  return (
    <SectionCard title={t("Device Settings")} sx={{ gridArea: "settings" }}>
      <FieldColumns>
        <Stack sx={{ gap: 0.5 }}>
          <Typography>{t("Disable Sleep Tracking")}</Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Box>
              <SwitchInput
                label={t("Disable Sleep Tracking Enabled")}
                hideLabel
                fieldName={"settings.user.schedule.0.enabled"}
                control={control}
              />
            </Box>
            <TextInput
              fieldName={"settings.user.schedule.0.timespan.start"}
              control={control}
              label={t("Disable Sleep Tracking From")}
              hideLabel
              variant={"outlined"}
              type={"time"}
              disabled={!disableSleepTracking}
            />
            <TextInput
              fieldName={"settings.user.schedule.0.timespan.end"}
              control={control}
              label={t("Disable Sleep Tracking To")}
              hideLabel
              variant={"outlined"}
              type={"time"}
              disabled={!disableSleepTracking}
            />
          </Box>
        </Stack>
        <SliderInput
          fieldName={"settings.user.radar.distance_limit"}
          control={control}
          label={`${t("Distance")} (${t("meter")})`}
          min={XETHRU_CHIP_MIN_DISTANCE}
          max={3}
          step={0.1}
          decimals={1}
          unit={t("meter_abbreviation_lc")}
        />
        <SliderInput
          fieldName={"settings.user.display.intensity"}
          control={control}
          label={t("Display Intensity")}
          min={1}
          max={100}
          hide={!isSM100}
        />
        <SelectInput
          fieldName={"settings.user.display.mode"}
          control={control}
          label={t("Display Mode")}
          hide={!isSM100}
          options={[
            {
              id: "normal",
              name: t("Clock and circle"),
            },
            {
              id: "circle",
              name: t("Circle only"),
            },
            {
              id: "off",
              name: t("Off"),
            },
          ]}
        />
        <SwitchInput
          fieldName={"settings.user.audio.enabled"}
          control={control}
          label={t("Audio")}
          hide={!isSM100}
        />
        <SelectInput
          fieldName={"settings.user.time.format"}
          control={control}
          label={t("Time Format")}
          hide={!isSM100}
          options={[
            { id: "12", name: t("AM/PM") },
            { id: "24", name: t("24 Hour") },
          ]}
        />
        <SelectInput
          fieldName={"settings.user.time.timezone"}
          control={control}
          label={t("time_zones")}
          placeholder={t("Automatic")}
          options={[
            { id: "", label: t("Automatic") },
            ...TIMEZONES.map(({ label, value }) => ({
              id: value,
              label: label,
            })),
          ]}
        />
      </FieldColumns>
    </SectionCard>
  );
}
